"use client";

import { useEffect, useRef, useState } from "react";

import AuthForm from "../auth/AuthForm";

/**
 * Sign-in in a native <dialog>, which brings focus trapping, Escape to close
 * and an inert page behind it for free, rather than a hand-built modal that
 * has to promise them.
 */
export default function AuthDialog({
  open,
  onClose,
  next,
  fileName,
  googleEnabled,
  mailEnabled,
}: {
  open: boolean;
  onClose: () => void;
  next: string;
  fileName: string;
  googleEnabled: boolean;
  mailEnabled: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [mode, setMode] = useState<"signup" | "signin">("signup");

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal?.();
    if (!open && dialog.open) dialog.close?.();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => {
        // A click on the backdrop lands on the dialog element itself.
        if (e.target === ref.current) onClose();
      }}
      aria-label={mode === "signup" ? "Create an account" : "Sign in"}
      className="m-auto w-[calc(100%-2rem)] max-w-lg bg-transparent p-0 text-paper"
    >
      <div className="relative">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center bg-ink font-body text-xl text-paper hover:bg-paper/15"
        >
          ×
        </button>
        <AuthForm
          mode={mode}
          googleEnabled={googleEnabled}
          mailEnabled={mailEnabled}
          next={next}
          heading={
            mode === "signup"
              ? "Create an account to rebuild it"
              : "Sign in to rebuild it"
          }
          blurb={`Your first reading is free. ${fileName} is saved on this device, and it comes with you.`}
          onSwitch={() =>
            setMode((m) => (m === "signup" ? "signin" : "signup"))
          }
        />
      </div>
    </dialog>
  );
}
